import NotificationBase, {
  NotificationMessage,
  onMessageHandler,
  onClickNotificationHandler,
  TokenType,
} from './NotificationBase';

// TWA PostMessage API 브릿지.
//
// 안전성 보장: MessagePort는 Chrome이 assetlinks.json의 `use_as_origin`
// relation을 Android 앱의 서명 인증서와 대조 검증한 뒤에만 페이지에 전달됨.
// 실제 Chrome이 찍어주는 `event.origin` 은 `android-app://<웹 origin의
// 호스트명>` 형식 (예: `android-app://study.mildang.kr`). 이는 TWA가 해당
// 웹 origin을 proxy한다는 의미를 반영한 Chrome의 내부 표현이며,
// `event.source === null` 과 결합하면 페이지 스크립트가 합성 불가능한
// 런타임 보증을 제공한다 (iOS의 `window.webkit.messageHandlers` 존재
// 체크와 동급).
const EXPECTED_ORIGINS = [
  'https://study.mildang.kr',
  'android-app://study.mildang.kr',
  // Chrome 구현이 바뀌어 패키지명을 쓸 가능성에 대비
  'android-app://kr.mildang.student.android',
];
const HANDSHAKE_TIMEOUT_MS = 5000;

// 메시지 타입 상수. Android 쪽 BridgeProtocol과 1:1로 일치시켜야 함.
const PUSH_PERMISSION_REQUEST = 'push-permission-request';
const PUSH_TOKEN = 'push-token';
const PUSH_NOTIFICATION = 'push-notification';
const PUSH_NOTIFICATION_CLICK = 'push-notification-click';
const PUSH_SET_BADGE_COUNT = 'push-set-badge-count';
const PUSH_PERMISSION_STATE = 'push-permission-state';
const PUSH_CLEAR_NOTIFICATIONS_BY_CHANNEL = 'push-clear-notifications-by-channel';
const PUSH_CLEAR_ALL_NOTIFICATIONS = 'push-clear-all-notifications';
const ACTIVE_CHANNEL_UPDATE = 'active-channel-update';

type BridgeMessage = {
  type: string;
  payload?: any;
};

let bridgePort: MessagePort | null = null;
let bridgeReadyPromise: Promise<MessagePort | null> | null = null;
const portUpdateHandlers: Set<(port: MessagePort) => void> = new Set();

function onBridgePortUpdate(handler: (port: MessagePort) => void): () => void {
  portUpdateHandlers.add(handler);
  return () => {
    portUpdateHandlers.delete(handler);
  };
}

type EarlyBridgeEntry = {
  port: MessagePort;
  origin: string;
  data?: unknown;
  ts?: number;
};

function drainEarlyBridgeBuffer(
  onPortFound: (port: MessagePort, origin: string) => void,
): void {
  if (typeof window === 'undefined') return;
  const buffer = (window as unknown as { __androidBridgePorts__?: EarlyBridgeEntry[] })
    .__androidBridgePorts__;
  if (!Array.isArray(buffer) || buffer.length === 0) return;
  console.log('[AndroidBridge] draining early buffer, entries=' + buffer.length);
  while (buffer.length > 0) {
    const entry = buffer.shift();
    if (!entry) continue;
    if (!entry.port) continue;
    if (!EXPECTED_ORIGINS.includes(entry.origin)) {
      console.log(
        '[AndroidBridge] early buffer entry origin not in allowlist: ' + entry.origin,
      );
      continue;
    }
    onPortFound(entry.port, entry.origin);
  }
}

function establishBridge(): Promise<MessagePort | null> {
  if (bridgeReadyPromise) return bridgeReadyPromise;

  bridgeReadyPromise = new Promise<MessagePort | null>((resolve) => {
    if (typeof window === 'undefined') {
      resolve(null);
      return;
    }

    let firstResolved = false;
    let timeoutId: number | undefined;

    // 페이지 수명 동안 리스너를 유지한다. 백그라운드 알림 탭으로 TWA 탭이
    // 태스크에서 제거되고 LauncherActivity가 TWA를 재실행하면 Chrome이
    // 새 CustomTabsSession과 새 MessagePort를 발급하므로, 교체된 포트를
    // 받아들이지 않으면 웹→네이티브 송신이 소리 없이 실패한다.
    const onMessage = (e: MessageEvent) => {
      // 디버그: Chrome이 실제로 어떤 origin/port 형태로 전달하는지 모든
      // message 이벤트를 로깅해 확인.
      console.log('[AndroidBridge] message event', {
        origin: e.origin,
        hasPorts: !!(e.ports && e.ports.length > 0),
        portCount: e.ports ? e.ports.length : 0,
        dataType: typeof e.data,
        dataPreview: typeof e.data === 'string' ? e.data.slice(0, 200) : e.data,
        source: e.source ? 'present' : 'null',
      });

      if (!e.ports || !e.ports[0]) {
        // 포트가 없는 일반 window.postMessage 는 브릿지 핸드셰이크가 아님.
        return;
      }
      // 포트가 실려 있다면 후보. origin 이 허용 목록에 없으면 스푸핑 의심
      // (페이지 스크립트는 ports 를 실은 message 를 합성할 수 없지만, 같은
      // origin 의 다른 컨텍스트일 수 있으므로 확인).
      if (!EXPECTED_ORIGINS.includes(e.origin)) {
        console.log(
          '[AndroidBridge] port arrived but origin not in allowlist: ' + e.origin,
        );
        return;
      }

      const port = e.ports[0];
      console.log('[AndroidBridge] port received, adopting');
      bridgePort = port;
      portUpdateHandlers.forEach((h) => {
        try {
          h(port);
        } catch {
          // 소비자 쪽 예외는 무시
        }
      });

      if (!firstResolved) {
        firstResolved = true;
        if (timeoutId !== undefined) window.clearTimeout(timeoutId);
        resolve(port);
      }
    };

    window.addEventListener('message', onMessage);
    console.log('[AndroidBridge] listener attached, awaiting MessagePort...');

    // 번들 parsing 전에 <head> 인라인 스크립트가 잡아둔 port 이벤트를 이제
    // 처리. JS는 단일 스레드라 addEventListener 직후에 drain 해야 listener
    // 등록과 drain 사이에 새 이벤트가 끼어들 여지가 없다.
    drainEarlyBridgeBuffer((port, origin) => {
      console.log('[AndroidBridge] adopting port from early buffer, origin=' + origin);
      bridgePort = port;
      portUpdateHandlers.forEach((h) => {
        try {
          h(port);
        } catch {
          // 소비자 쪽 예외는 무시
        }
      });
      if (!firstResolved) {
        firstResolved = true;
        if (timeoutId !== undefined) window.clearTimeout(timeoutId);
        resolve(port);
      }
    });

    timeoutId = window.setTimeout(() => {
      if (firstResolved) return;
      firstResolved = true;
      console.log('[AndroidBridge] handshake timeout after ' + HANDSHAKE_TIMEOUT_MS + 'ms');
      // 리스너는 제거하지 않는다 — 뒤늦게 도착한 핸드셰이크도 유효하게 처리.
      resolve(null);
    }, HANDSHAKE_TIMEOUT_MS);
  });

  return bridgeReadyPromise;
}

export async function isAndroidEnv(): Promise<boolean> {
  const port = await establishBridge();
  return port !== null;
}

export class AndroidNotification extends NotificationBase {
  private port: MessagePort | null = null;
  private messageHandlers: Set<onMessageHandler> = new Set();
  private clickHandlers: Set<onClickNotificationHandler> = new Set();
  private pendingResponses: Map<string, (value: unknown) => void> = new Map();
  private bufferedClick: NotificationMessage | null = null;

  constructor() {
    super();
    void this.init();
  }

  private async init() {
    const port = await establishBridge();
    if (!port) return;
    this.adoptPort(port);
    // Chrome이 새 포트를 넘겨줄 때마다(예: 백그라운드 상태에서 알림 탭으로
    // 인해 TWA가 재실행된 경우) 현재 포트를 교체한다.
    onBridgePortUpdate((replacement) => this.adoptPort(replacement));
  }

  private adoptPort(port: MessagePort) {
    this.port = port;
    this.port.onmessage = this.handlePortMessage;
    this.port.start?.();
  }

  private handlePortMessage = (e: MessageEvent) => {
    let msg: BridgeMessage | null = null;
    try {
      msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
    } catch {
      return;
    }
    if (!msg || !msg.type) return;

    const pending = this.pendingResponses.get(msg.type);
    if (pending) {
      this.pendingResponses.delete(msg.type);
      pending(msg.payload?.detail);
    }

    if (msg.type === PUSH_NOTIFICATION) {
      const detail = msg.payload?.detail as NotificationMessage | undefined;
      if (detail) {
        this.messageHandlers.forEach((h) => {
          try {
            h(detail);
          } catch {
            // 소비자 쪽 예외는 무시
          }
        });
      }
    }

    if (msg.type === PUSH_NOTIFICATION_CLICK) {
      const detail = msg.payload?.detail as NotificationMessage | undefined;
      if (detail?.channelId) {
        if (this.clickHandlers.size === 0) {
          // 핸드셰이크가 먼저 끝난 뒤에 React 컴포넌트가 마운트되며 구독을
          // 시작하므로, 구독 이전에 도착한 콜드스타트 클릭 이벤트를 최신 것
          // 하나만 버퍼링해 두고 첫 구독자에게 즉시 재전달한다.
          this.bufferedClick = { ...detail, channelId: detail.channelId };
        } else {
          this.clickHandlers.forEach((h) => {
            try {
              h({ ...detail, channelId: detail.channelId });
            } catch {
              // 소비자 쪽 예외는 무시
            }
          });
        }
      }
    }
  };

  private async ensurePort(): Promise<MessagePort | null> {
    if (this.port) return this.port;
    return await establishBridge();
  }

  private async fireAndForget(type: string, payload?: unknown): Promise<void> {
    const port = await this.ensurePort();
    if (!port) return;
    port.postMessage(JSON.stringify({ type, payload }));
  }

  private async requestWithResponse<T>(
    type: string,
    payload?: unknown,
    timeoutMs = 3000,
  ): Promise<T | undefined> {
    const port = await this.ensurePort();
    if (!port) return undefined;
    return await new Promise<T | undefined>((resolve) => {
      let timer: number | undefined;
      const wrapped = (value: unknown) => {
        if (timer !== undefined) window.clearTimeout(timer);
        resolve(value as T);
      };
      this.pendingResponses.set(type, wrapped);
      timer = window.setTimeout(() => {
        if (this.pendingResponses.get(type) === wrapped) {
          this.pendingResponses.delete(type);
          resolve(undefined);
        }
      }, timeoutMs);
      port.postMessage(JSON.stringify({ type, payload }));
    });
  }

  async requestPermission(): Promise<NotificationPermission> {
    const detail = await this.requestWithResponse<string>(PUSH_PERMISSION_REQUEST);
    switch (detail) {
      case 'granted':
        return 'granted';
      case 'denied':
        return 'denied';
      default:
        return 'default';
    }
  }

  async getPermissionState(): Promise<NotificationPermission> {
    const detail = await this.requestWithResponse<string>(PUSH_PERMISSION_STATE);
    switch (detail) {
      case 'granted':
        return 'granted';
      case 'denied':
        return 'denied';
      default:
        return 'default';
    }
  }

  async requestToken(): Promise<TokenType | null> {
    const token = await this.requestWithResponse<string>(PUSH_TOKEN, undefined, 5000);
    if (!token) return null;
    return { platform: 'android', token };
  }

  async onMessage(handler: onMessageHandler) {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  setBadgeCount(count: number) {
    void this.fireAndForget(PUSH_SET_BADGE_COUNT, { count });
  }

  onNotificationClick(handler: onClickNotificationHandler) {
    this.clickHandlers.add(handler);
    if (this.bufferedClick) {
      const buffered = this.bufferedClick;
      this.bufferedClick = null;
      try {
        handler(buffered);
      } catch {
        // 소비자 쪽 예외는 무시
      }
    }
    return () => {
      this.clickHandlers.delete(handler);
    };
  }

  async clearNotificationsByChannel(channelId: string): Promise<void> {
    await this.requestWithResponse(PUSH_CLEAR_NOTIFICATIONS_BY_CHANNEL, { channelId }, 1500);
  }

  async clearAllNotifications(): Promise<void> {
    await this.requestWithResponse(PUSH_CLEAR_ALL_NOTIFICATIONS, undefined, 1500);
  }

  supportsNotifications(): boolean {
    return true;
  }

  setActiveChannel(channelId: string | null): void {
    void this.fireAndForget(ACTIVE_CHANNEL_UPDATE, { channelId: channelId ?? null });
  }
}

export default AndroidNotification;
